import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { documentRevisionsActions } from '../../../state/ducks/documentRevisions';
import { DocumentTypeByIdForm } from '../../../state/ducks/documentRevisions/documentType/types';
import { DocumentRevision, DocumentRevisionStatus } from '../../../state/ducks/documentRevisions/types';
import Text from '../../components/Text';
import { FormContext } from '../../components/forms/FormContext';
import AutocompleteField from '../../components/forms/fields/Autocomplete/Autocomplete';
import { checkIsDocumentLHRT } from '../../documentRevision/helpers/checkDocumentGroup';
import useActionCreator from '../../hooks/useActionCreator';
import useAsync from '../../hooks/useAsync';
import { DialogHandler } from '../../hooks/useDialog';
import { styles as useStyles } from './FormListDialog/FormListDialog.styles';

interface LHRDeviationDialogProps {
  dialog: DialogHandler
  lhrtForms: DocumentTypeByIdForm[]
  // released forms whose output is the deviation; omit when the form is already known (creating from the form page)
  deviationForms?: DocumentTypeByIdForm[]
  isLoading: boolean
  onSelect: (lhrtRevId: string, formId?: string) => void
}

const LHRDeviationDialog: React.FC<LHRDeviationDialogProps> = ({
  dialog,
  lhrtForms,
  deviationForms,
  isLoading,
  onSelect,
}) => {
  const classes = useStyles();
  const [formId, setFormId] = useState('');
  const [lhrtId, setLhrtId] = useState('');
  // shown whenever opened from the landing page, even with no forms; only required when there are some
  const showFormField = Boolean(deviationForms);
  const needsForm = Boolean(deviationForms?.length);

  useEffect(() => {
    setFormId('');
    setLhrtId('');
  }, [dialog.isOpen]);

  // create once everything required is picked
  useEffect(() => {
    if (lhrtId && (formId || !needsForm)) {
      onSelect(lhrtId, formId || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, lhrtId]);
  const [parts, setParts] = useState<DocumentRevision[]>([]);
  const fetchAvailablePartsAction = useActionCreator(documentRevisionsActions.fetchAvailableParts);

  const fetchAvailablePartsAsync = useAsync({
    onSuccess: (data: DocumentRevision[] = []) => {
      setParts(data);
    },
  });

  useEffect(() => {
      fetchAvailablePartsAsync.start(
        fetchAvailablePartsAction,
        fetchAvailablePartsAsync,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const checkForPart = (form: DocumentTypeByIdForm) => {
      const id = form.formTemplate?.schema?.find((item) => item.name === 'lhr-selected-part')?.editorConfig?.id;
      return parts.find((part) => part.document.id === id && part.status === DocumentRevisionStatus.Released);
    };

  const renderPartLabel = (form: DocumentTypeByIdForm) => {
    const partDocRev = checkForPart(form);
      if (!partDocRev) {
        return `${form.document.docId} - ${form.name}`;
      }
      return `(${partDocRev?.document.docId} rev ${partDocRev?.displayRevision} - ${partDocRev?.name}) ${form.name}`;
    };

  const renderOptions = () => {
      const LHRForms = lhrtForms.filter((form) => {
        const partDocRev = checkForPart(form);
        const isLHRT = checkIsDocumentLHRT(form.document.documentType?.groupOptions);
        return (isLHRT && partDocRev) || (!isLHRT && !partDocRev);
      });

      return LHRForms.map((form) => ({
        label: renderPartLabel(form),
        value: form.id,
      }));
    };

  return (
    <Dialog
      open={dialog.isOpen}
      onClose={dialog.close}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <Text translation="deviation.select.lhrt.title" />
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        {isLoading ? (
          <Box className={classes.loading}>
            <CircularProgress size={20} />
            <Text translation="common.loading" />
          </Box>
        ) : (
          <FormContext.Provider value={{ submitOnChange: false }}>
            <Formik initialValues={{}} onSubmit={() => undefined}>
              {() => (
                <>
                  {showFormField && (
                    <AutocompleteField
                      name="selectForm"
                      size="sm"
                      portal
                      autocompleteProps={{ id: 'deviation-dialog-form-select' }}
                      options={(deviationForms ?? []).map((form) => ({ label: form.name, value: form.id }))}
                      onClick={(option: { value: string }) => setFormId(option.value)}
                      label="supplier.select.form"
                    />
                  )}
                  <AutocompleteField
                    name="selectLHRT"
                    size="sm"
                    portal
                    autocompleteProps={{ id: 'deviation-dialog-lhr-select' }}
                    options={renderOptions()}
                    onClick={(option: { value: string }) => setLhrtId(option.value)}
                    label="deviation.select.lhrt.label"
                  />
                </>
              )}
            </Formik>
          </FormContext.Provider>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={dialog.close}
          color="secondary"
          className={classes.cancelButton}
        >
          <Text translation="common.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LHRDeviationDialog;
